import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocalStorageByUser } from 'common/hooks';

const STORAGE_KEY = 'scalar_desktop_notifications_enabled';
const SW_PATH = '/notification-sw.js';

function getInitialPermission(): NotificationPermission {
  if (typeof Notification === 'undefined') {
    return 'denied';
  }
  return Notification.permission;
}

export interface BrowserNotificationsState {
  enabled: boolean;
  isReady: boolean;
  permission: NotificationPermission;
  requestAndEnable: () => Promise<void>;
  disable: () => void;
  fire: (title: string, body: string) => void;
}

export function useBrowserNotifications(): BrowserNotificationsState {
  const [permission, setPermission] = useState<NotificationPermission>(getInitialPermission);
  const [storedEnabled, setStoredEnabled, isReady] = useLocalStorageByUser(STORAGE_KEY, false);
  const enabled = isReady && storedEnabled && permission === 'granted';

  // Register SW once so showNotification() works (new Notification() is deprecated in Chrome main thread)
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register(SW_PATH).catch(() => {});
    }
  }, []);

  // Sync enabled → false if permission was revoked externally
  useEffect(() => {
    if (isReady && permission !== 'granted' && storedEnabled) {
      setStoredEnabled(false);
    }
  }, [isReady, permission, setStoredEnabled, storedEnabled]);

  const requestAndEnable = useCallback(async () => {
    if (typeof Notification === 'undefined') {
      return;
    }
    const perm = await Notification.requestPermission();
    setPermission(perm);
    if (perm === 'granted') {
      setStoredEnabled(true);
    }
  }, [setStoredEnabled]);

  const disable = useCallback(() => {
    setStoredEnabled(false);
  }, [setStoredEnabled]);

  const stateRef = useRef({ enabled, isReady, permission });
  useEffect(() => {
    stateRef.current = { enabled, isReady, permission };
  }, [enabled, isReady, permission]);

  const fire = useCallback((title: string, body: string) => {
    const { enabled: en, isReady: ready, permission: perm } = stateRef.current;
    if (!ready || !en || perm !== 'granted' || typeof Notification === 'undefined') {
      return;
    }

    // Service worker notifications properly reach macOS Notification Center.
    // new Notification() from the main thread is deprecated in Chrome and bypasses the OS.
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready
        .then(reg => reg.showNotification(title, { body, icon: '/favicon.ico' }))
        .catch(() => {
          try {
            new Notification(title, { body }); // eslint-disable-line no-new
          } catch {} // eslint-disable-line no-empty
        });
    } else {
      try {
        new Notification(title, { body }); // eslint-disable-line no-new
      } catch {} // eslint-disable-line no-empty
    }
  }, []);

  return { enabled, isReady, permission, requestAndEnable, disable, fire };
}
